import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { Trash as TrashIcon } from "@phosphor-icons/react/Trash";
import { Warning as WarningIcon } from "@phosphor-icons/react/Warning";
import { ConfirmationModal } from "../../components/confirmation-modal";
import type { Account, PartnerWriteError } from "../partner-templates/types";

export type PartnerCancellationVariant =
	| "cancel-paid"
	| "end-trial-early"
	| "delete-account";

export interface PartnerCancellationTemplateProps {
	open: boolean;
	variant: PartnerCancellationVariant;
	account: Account;
	loading?: boolean;
	error?: PartnerWriteError | null;
	confirmText?: string;
	requireTypedConfirmation?: boolean;
	/**
	 * Fires `cancelManagedAccountSubscription(accountId)` per spec §4.3 when
	 * variant is "cancel-paid".
	 */
	onCancelSubscription?: (accountId: string) => void;
	/**
	 * Fires `endManagedAccountTrialEarly(accountId)` per spec §4.3 when variant
	 * is "end-trial-early".
	 */
	onEndTrialEarly?: (accountId: string) => void;
	/**
	 * Fires `deleteManagedAccount(accountId)` per spec §4.9 when variant is
	 * "delete-account".
	 */
	onDelete?: (accountId: string) => void;
	onClose: () => void;
}

const COPY: Record<
	PartnerCancellationVariant,
	{
		title: string;
		message: string;
		actionLabel: string;
		iconColor: "warning" | "error";
	}
> = {
	"cancel-paid": {
		title: "Cancel subscription?",
		message:
			"Cancelling will end the active subscription and close this client account. The account stays in your portfolio in a closed state until deleted.",
		actionLabel: "Cancel subscription",
		iconColor: "warning",
	},
	"end-trial-early": {
		title: "End trial early?",
		message:
			"The trial will end immediately. The client account will be in an expired-trial state — you can subsequently activate it to paid or delete it.",
		actionLabel: "End trial",
		iconColor: "warning",
	},
	"delete-account": {
		title: "Delete client account?",
		message:
			"Deleting terminates any active subscription and removes the account from your portfolio. This cannot be undone.",
		actionLabel: "Delete account",
		iconColor: "error",
	},
};

export function PartnerCancellationTemplate({
	open,
	variant,
	account,
	loading = false,
	error = null,
	confirmText,
	requireTypedConfirmation = false,
	onCancelSubscription,
	onEndTrialEarly,
	onDelete,
	onClose,
}: PartnerCancellationTemplateProps): React.JSX.Element {
	const copy = COPY[variant];
	const [typed, setTyped] = React.useState("");

	React.useEffect(() => {
		if (!open) setTyped("");
	}, [open]);

	const canConfirm = requireTypedConfirmation
		? typed.trim().toLowerCase() === account.companyName.trim().toLowerCase()
		: true;

	const fireMutation = () => {
		if (variant === "cancel-paid") onCancelSubscription?.(account.id);
		else if (variant === "end-trial-early") onEndTrialEarly?.(account.id);
		else if (variant === "delete-account") onDelete?.(account.id);
	};

	return (
		<ConfirmationModal
			open={open}
			title={copy.title}
			message={copy.message}
			loading={loading}
			error={error?.userMessage ?? null}
			iconColor={copy.iconColor}
			icon={
				variant === "delete-account" ? (
					<TrashIcon size={24} />
				) : (
					<WarningIcon size={24} />
				)
			}
			items={[
				{ id: account.id, title: account.companyName, subtitle: account.slug },
			]}
			primaryAction={{
				label: confirmText ?? copy.actionLabel,
				color: copy.iconColor === "error" ? "error" : "warning",
				variant: "contained",
				onClick: () => canConfirm && fireMutation(),
			}}
			secondaryAction={{
				label: "Keep account",
				onClick: onClose,
				variant: "text",
			}}
			onClose={onClose}>
			{requireTypedConfirmation && (
				<Box sx={{ mt: 2 }}>
					<Stack spacing={1}>
						<Typography variant="body2">
							Type <strong>{account.companyName}</strong> to confirm.
						</Typography>
						<TextField
							value={typed}
							onChange={(e) => setTyped(e.target.value)}
							size="small"
							fullWidth
							label="Account name"
							placeholder={account.companyName}
							disabled={loading}
						/>
					</Stack>
				</Box>
			)}
		</ConfirmationModal>
	);
}
